import {
  useSectionQuery,
  useSectionSummaryQuery,
  type ClinicalSummarySectionSlug,
} from "@/api/hooks";
import {
  useLibraryAssetsQuery,
  useLibraryPlacementRegistryQuery,
  useProjectAdoptionsQuery,
} from "@/api/library";
import { lockReasonsByPath } from "@/lib/fieldState";
import { sectionReviewRows, settledRead } from "@/lib/sectionAdoption";
import { SectionAdoptionPanel } from "@/ui/adoptions/SectionAdoptionPanel";
import { ConversationLanguageControl } from "@/ui/project/ConversationLanguageControl";
import { FinalQuestioningControl } from "@/ui/project/FinalQuestioningControl";
import {
  pathwayFlowControls,
  pathwayFlowPanel,
  usePathwayFlow,
} from "@/ui/project/PathwayFlow";
import { ResponseFilteringControl } from "@/ui/project/ResponseFilteringControl";
import { BgSetAdoptionPicker } from "@/ui/adoptions/BgSetAdoptionPicker";
import { SectionReview } from "@/ui/review/SectionReview";

import { FieldAnchorProvider } from "./FieldAnchorContext";
import { ValidationPanel } from "./ValidationPanel";

/**
 * Diagnostics for one clinical section: the effective projection, the
 * validation report, and the join between them.
 *
 * The join is the substance of #7. A locked-floor diagnostic exists in neither
 * resource alone — `ValidationReport` says a rule was violated, and
 * `FieldState.lock.reason` says the field carries the clinical safety floor.
 * Only together do they identify the locked rule, which is what the acceptance
 * criterion "guard-floor violations identify the locked rule without exposing
 * internal secrets" asks for. Nothing beyond `code`, `message`, and the lock
 * reason is surfaced.
 *
 * Both halves fail independently and honestly, and the report may be absent,
 * stale, or unclassifiable. Neither failure is allowed to make the other look
 * resolved.
 *
 * The effective projection no longer "returns 501 today", as this paragraph
 * claimed until #373: #29 G3 implemented the endpoint. What it answers now is
 * a 200 where an engine and pack mounts are configured, a typed
 * `409 projection_identity_mismatch` where a stored identity is behind the
 * runtime, a plain 409 where stored state cannot be composed, and
 * `503 upstream_unavailable` where the composition environment is unreachable
 * — which is every lane that mounts no engine, and why this surface renders a
 * transient failure with a retry rather than a terminal one without.
 *
 * This body keeps routing all of that through `QueryErrorState`: it serves six
 * slugs with one set of copy and has nothing to say about a projection
 * identity. `ui/summary/SummaryContent.tsx` is the surface that draws those
 * distinctions, for the one slug whose acceptance criteria ask for them.
 *
 * #443 adds the adoption block above the effective-fields list. It is the same
 * section projection this body already reads, asked a different question —
 * which shared-library item supplies this section, at which adopted version,
 * in which state — joined to the pin set and the catalog. It renders OUTSIDE
 * the branch below, because the state it has to render most carefully is the
 * one this lane always produces: a projection that could not be read must say
 * so rather than imply the section adopts nothing.
 */
export function SectionDiagnostics({
  projectId,
  section,
  sectionLabel,
}: {
  projectId: string;
  /**
   * NARROWER than `SectionSlug` since #488: this body serves the six sections
   * that have a bounded review contract. `summary` is a `SectionSlug` with no
   * summary contract — the service answers 422 for it — and `SectionSurface`
   * routes it to `ClinicalSummary` before this body is reached. The type says
   * so, rather than leaving the guarantee to the caller's branch order.
   */
  section: ClinicalSummarySectionSlug;
  sectionLabel: string;
}) {
  // BG sets are the project's independent library pins. Their list and its
  // actions come entirely from the catalog + adoption reads below; neither
  // needs the clinical engine to compose a projection. Asking for that
  // projection here made an engine outage hide healthy stored adoptions behind
  // a 503. The other sections still need it for their lock/provenance join.
  const sectionQuery = useSectionQuery(
    projectId,
    section === "bg-sets" ? null : section,
  );
  // #443's two extra reads. Both are shared cache entries — the same keys
  // `/adoptions` and the library catalog are read under everywhere else — so a
  // clinician moving between sections, the Clinical summary's whole-pin-set
  // view and the Questionnaires destination observes one pin set rather than
  // several. (#489 retired the Adopted content tab this note used to name; the
  // property it describes is unchanged and now matters more, because the
  // adoption ACTION reads the same entry.)
  const adoptions = useProjectAdoptionsQuery(projectId);
  const catalog = useLibraryAssetsQuery(true);
  // The SERVED cardinality policy (#634), held here with the other reads and
  // passed down as a boolean: `SectionReview` is prop-driven by design, and the
  // row's Adopt states a refusal rather than opening a confirmation that could
  // not describe what it displaces. Removal is never gated on it (#612).
  const policyUnread = !useLibraryPlacementRegistryQuery().isSuccess;
  // #488's bounded review read, held HERE rather than inside `SectionReview`
  // since round 2. Two surfaces on this screen have to agree about it: the
  // review body renders the rows, and the adoption panel must not offer a
  // second update/remove control for an item whose row is one of them. Passing
  // one result to both is the same rule the pin set and the catalog already
  // follow — the alternative is a second cache observer of a key this screen
  // already watches, which is what that rule exists to prevent.
  // The bounded-summary contract remains authoritative for every other
  // section. BG-set row identity is already authoritative in the adoption
  // resource itself, so composing the same rows through the engine is both
  // redundant and a false availability dependency.
  const summaryQuery = useSectionSummaryQuery(
    projectId,
    section,
    section !== "bg-sets",
  );
  // #855's typed flow read, held HERE with the other four for the reason this
  // body already holds them: the Pathways rows are GROUPED by the service's own
  // Always/automatic selection and the controls above them edit the slot list
  // that produces it, so one observation of the read serves both. A second one
  // is two things that can disagree in front of a clinician.
  //
  // Enabled on `pathways` alone. The Rules language control holds its own
  // observation of the same key — it is a self-contained compact row, exactly
  // as `ResponseFilteringControl` is — and TanStack de-duplicates the request
  // when both are mounted, which they never are: no section is both.
  const flow = usePathwayFlow(projectId, { enabled: section === "pathways" });

  // The projection is READ but no longer RENDERED (#488) for the five bounded
  // engine-composed sections. It stays there because it is the only source of
  // `lock.reason`: the locked-floor diagnostic exists in neither the
  // validation report nor the projection alone. BG sets intentionally skip it
  // above; their independent asset rows do not derive lock or identity from a
  // composed projection.
  const fields = sectionQuery.data?.fields ?? [];
  const lockReasonByPath = lockReasonsByPath(fields);

  // WHICH ITEMS THE CANONICAL ROWS SPEAK FOR, and therefore which ones the
  // panel above must stay silent about (Codex, #570 round 2).
  //
  // Until #612 this mirrored `SectionReview`'s branch condition in place —
  // rows existed only when the bounded read succeeded and composed something.
  // The row set is now a compound rule (a failed, pending or composed-empty
  // read falls back to the STORED pin set, which is #612's recovery surface),
  // so the rule is EXPORTED and read here rather than restated: the ids below
  // are exactly the rows `SectionReview` will render, from the same inputs.
  // The inputs come through `settledRead` — the dialog's own `isSuccess`
  // rule, and the same function `SectionReview` reads them with — so a failed
  // refetch that leaves `.data` in place is treated identically on both
  // surfaces: the body withholds actions, and a panel that went silent over a
  // different reading of the same read would leave an item with no control
  // anywhere. With #615 the pin set alone yields a row (its `assetType` names
  // the family), so a row exists — and the panel's duplicate link is
  // withheld — through a catalog outage too.
  //
  // Empty set when nothing is renderable, which is the FALLBACK the
  // consolidation keeps: with no canonical row, the panel's own update/remove
  // controls are the only place the decision exists, exactly as #489 shipped
  // them.
  //
  // EVERY item the body renders, not the adopted ones (#570 round 3). The
  // service reports an UNADOPTED seed-catalog entry as a `supplyState: "base"`
  // row and the row offers `Adopt v<N>` for it, so that id belongs here exactly
  // as an adopted one does — the panel would otherwise list the same unpinned
  // asset as an adopt-new candidate and offer a second control for it. Mapping
  // `items` whole is what makes the two cases one rule instead of two.
  const canonicalItemIds = new Set(
    section === "bg-sets"
      ? []
      : sectionReviewRows({
          summary: settledRead(summaryQuery),
          pins: settledRead(adoptions)?.items ?? null,
          // The token from the SAME read as the pins (#618). A CONVERGING row
          // stays in this set deliberately: it is still the canonical row for
          // that item, and it is withholding its own adopt/update control on
          // purpose — so the panel filling the gap with a second control is
          // exactly the drift this shared derivation exists to prevent. One
          // surface owns the decision, and right now it is declining to offer
          // it because the read it would be taken against has not settled.
          pinSetVersion: settledRead(adoptions)?.adoptionsVersion ?? null,
          catalog: settledRead(catalog)?.items ?? null,
          section,
        }).items.map((item) => item.id),
  );

  return (
    // One provider per section surface. Anchors registered by the fields below
    // are the only paths a finding can resolve to, so navigating away from a
    // section correctly removes its jump affordances rather than leaving links
    // pointing at unmounted controls.
    <FieldAnchorProvider>
      <div className="space-y-6">
        {/*
          THE SHARED PANE, NOT A CARD PER SECTION (#909). What stood here was a
          `Card` whose title repeated the section's own name — already rendered
          by the breadcrumb leaf with `aria-current="page"` and by the current
          rail item — above a stack of adoption prose and then, finally, the
          compact rows. `ProjectSectionPane` is the arrangement the reference
          screens use: the adoption action top-right, the rows, and the
          evidence in a `Details` disclosure. The pane is rendered by the
          adoption components themselves so that each one's action, controls
          and evidence stay with the reads that produce them.
        */}
        {section === "bg-sets" ? (
          <BgSetAdoptionPicker
            projectId={projectId}
            sectionLabel={sectionLabel}
            adoptions={adoptions}
            catalog={catalog}
          />
        ) : (
          <SectionAdoptionPanel
            projectId={projectId}
            section={section}
            sectionLabel={sectionLabel}
            sectionQuery={sectionQuery}
            adoptions={adoptions}
            catalog={catalog}
            canonicalItemIds={canonicalItemIds}
            withDetails={section !== "pathways"}
            controls={
              // The typed settings each section owns, in `ProjectSectionPane`'s
              // declared `controls` slot — the insertion point #909 established
              // so they arrive without restructuring this body or the routes
              // above it. Per SECTION, because each is a control on that
              // section's own Lovable reference and the others have nothing to
              // say about it:
              //
              //  * `rules` — #855's Conversation language and #860's Response
              //    filtering (the bounded Output Guard signal list). The rows
              //    below stay #910's.
              //  * `safety` — #853's Final safety questioning, the scoped S7
              //    exception. It replaces nothing on the Safety screen: the
              //    adopted path rows and their adoption action are unchanged,
              //    and the reference's single `Enable runtime Safety` switch is
              //    corrected into this NAMED control plus the separate
              //    read-only runtime-detection state on the preview surface.
              //  * `pathways` — #855's dynamic-phase notices and pending-change
              //    bar. Its Interview tracks panel is not here: it arrives
              //    through `panel` below.
              section === "rules" ? (
                <>
                  <ConversationLanguageControl projectId={projectId} />
                  <ResponseFilteringControl projectId={projectId} />
                </>
              ) : section === "safety" ? (
                <FinalQuestioningControl projectId={projectId} />
              ) : section === "pathways" ? (
                // `undefined` where the dynamic phase has nothing to say, so
                // the pane opens no empty controls container above the rows.
                pathwayFlowControls(flow)
              ) : undefined
            }
          >
            {/*
              #488: the section's primary content is the BOUNDED review
              payload, not the effective projection. Its rows are also the ONE
              canonical place an existing item's adoption decision is taken —
              the adoption panel is told which ids they cover and offers no
              second update/remove control for them, in its Details or in its
              adopt-from-library picker (#909).

              What stood here was `SectionFieldList` over `sectionQuery.data
              .fields`: one read-only control per field, the field's value as
              text — prompt content, or `JSON.stringify` for anything
              structured — and its dotted address beneath it. That is the
              presentation #487 was built to replace and #488's amendment
              forbids on a Project route.

              `/section-summaries` is a read of its own rather than a fork on
              the projection query. The projection is still read above and still
              matters: it is the only source of `lock.reason`, which is what
              makes the locked-floor diagnostic in `ValidationPanel` a join
              rather than a guess. It is no longer RENDERED, which is the whole
              of the change.

              All three reads are passed rather than re-read, for the reason
              `SectionAdoptionPanel` records: its evidence block and these rows
              both decide adoptions, and two cache observers of one key are two
              things that can disagree in front of a clinician.
            */}
            <SectionReview
              projectId={projectId}
              section={section}
              sectionLabel={sectionLabel}
              summaryQuery={summaryQuery}
              adoptions={adoptions}
              catalog={catalog}
              policyUnread={policyUnread}
              // Absent until the flow read settles: a panel over a runtime fact not yet read would be a guess.
              panel={
                section === "pathways"
                  ? pathwayFlowPanel({
                      surface: flow,
                      catalog: catalog.data?.items ?? null,
                      projectId,
                    })
                  : undefined
              }
            />
          </SectionAdoptionPanel>
        )}

        <ValidationPanel
          projectId={projectId}
          lockReasonByPath={lockReasonByPath}
          // ONE SENTENCE (#909). The paragraph that stood here explained which
          // resource validation runs against and which one this surface does
          // not render — a contract note, in front of the findings a clinical
          // author has to act on. What survives is the fact that changes what
          // they do next: a finding names its field. The rest of the report's
          // technical evidence is in the panel's own Details.
          description="Validation runs against the whole draft. Each finding names the field it concerns."
        />
      </div>
    </FieldAnchorProvider>
  );
}
